import { useEffect, useMemo, useState } from 'react';
import { usePayouts } from '../../../../store/payouts';
import {
  FlowType,
  PayoutContactBankAccount,
  PayoutContactListItem,
  RequiredField,
  RequiredFieldsResponse,
} from '../../../../store/payouts/interfaces';
import { getEnabledCountriesForFlow, getPayoutCurrencyOptions } from '../create-steps/utils';
import { mapBankAccountToFieldValues } from './mappers';
import { BeneficiaryAccountMode, BeneficiaryFieldValues } from './types';

const isBankAccountMode = (mode: BeneficiaryAccountMode) =>
  mode === 'ADD_BANK_ACCOUNT' || mode === 'EDIT_BANK_ACCOUNT';

const flattenFields = (response: RequiredFieldsResponse) =>
  [
    ...(response.baseRequiredFields ?? []),
    ...(response.additionalRequiredFields ?? []),
    ...(response.allConfiguredFields ?? []),
  ].sort((a, b) => a.displayOrder - b.displayOrder);

export const useBeneficiaryAccountFlow = ({
  mode,
  contact,
  bankAccount,
}: {
  mode: BeneficiaryAccountMode;
  contact?: PayoutContactListItem;
  bankAccount?: PayoutContactBankAccount;
}) => {
  const { createPayout, getPayoutCountryConfigurations, loadBeneficiaryRequirements, loadBeneficiaryBanks } = usePayouts();
  const initialPayoutData = createPayout.createPayoutData;
  const [step, setStep] = useState<'country' | 'bank-data'>(
    isBankAccountMode(mode) ? 'bank-data' : createPayout.accountSubView
  );
  const [country, setCountry] = useState(
    bankAccount?.country || contact?.country || (mode === 'PAYOUT' ? initialPayoutData.transferCountry : '') || ''
  );
  const [flowType, setFlowType] = useState<FlowType>(
    contact?.flow_type || (mode === 'PAYOUT' ? initialPayoutData.flowType : undefined) || FlowType.B2C
  );
  const [fields, setFields] = useState<RequiredField[]>([]);
  const [bankOptions, setBankOptions] = useState<Array<{ code: string; name: string }>>([]);
  const [fieldValues, setFieldValues] = useState<BeneficiaryFieldValues>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<any>();

  useEffect(() => {
    if (!createPayout.enabledCountryConfigurations.data.length) {
      getPayoutCountryConfigurations();
    }
  }, []);

  useEffect(() => {
    if (contact) {
      setFlowType(contact.flow_type);
      if (mode !== 'EDIT_BANK_ACCOUNT') {
        setCountry(contact.country);
      }
    }
  }, [contact?.id, mode]);

  useEffect(() => {
    if (mode === 'EDIT_BANK_ACCOUNT' && bankAccount) {
      setCountry(bankAccount.country || contact?.country || '');
      setFieldValues(mapBankAccountToFieldValues(bankAccount));
    }
  }, [bankAccount?.id, contact?.country, mode]);

  const enabledCountries = useMemo(
    () => getEnabledCountriesForFlow(createPayout.enabledCountryConfigurations.data, flowType),
    [createPayout.enabledCountryConfigurations.data, flowType]
  );

  useEffect(() => {
    if (
      mode !== 'EDIT_BANK_ACCOUNT' &&
      createPayout.enabledCountryConfigurations.data.length &&
      country &&
      !enabledCountries.includes(country)
    ) {
      setCountry('');
    }
  }, [createPayout.enabledCountryConfigurations.data.length, enabledCountries, country, mode]);

  const currencyOptions = useMemo(
    () => getPayoutCurrencyOptions(createPayout.enabledCountryConfigurations.data, country, flowType),
    [createPayout.enabledCountryConfigurations.data, country, flowType]
  );
  const defaultCurrency = Object.keys(currencyOptions)[0] ?? '';

  const loadForm = (nextCountry = country, nextFlowType = flowType) => {
    if (!nextCountry) {
      return;
    }

    setLoading(true);
    setLoadError(undefined);
    let requirementsDone = false;
    let banksDone = false;
    const finish = () => {
      if (requirementsDone && banksDone) {
        setLoading(false);
        setStep('bank-data');
      }
    };
    const fail = (error: any) => {
      setLoadError(error);
      setLoading(false);
    };

    loadBeneficiaryRequirements(
      nextCountry,
      nextFlowType,
      response => {
        const excluded = new Set(['flow_type', 'country', 'amount', 'description']);
        const nextFields = flattenFields(response).filter(field => {
          if (excluded.has(field.fieldPath)) {
            return false;
          }
          if (isBankAccountMode(mode)) {
            return (
              field.fieldPath.startsWith('beneficiary.bank_account') ||
              field.fieldPath === 'beneficiary.bank_name' ||
              field.fieldPath === 'currency_to_pay'
            );
          }
          return true;
        });
        if (
          ['CREATE_CONTACT', 'PAYOUT'].includes(mode) &&
          !nextFields.some(field => field.fieldPath === 'beneficiary.relationship')
        ) {
          const emailIndex = nextFields.findIndex(field => field.fieldPath === 'beneficiary.email');
          const relationshipField = {
            fieldPath: 'beneficiary.relationship',
            required: true,
            dataType: 'SELECT' as const,
            description: 'Beneficiary relationship',
            displayOrder: (nextFields[emailIndex]?.displayOrder ?? 120) + 1,
          };
          if (emailIndex >= 0) {
            nextFields.splice(emailIndex + 1, 0, relationshipField);
          } else {
            nextFields.push(relationshipField);
          }
        }
        setFields(nextFields);
        requirementsDone = true;
        finish();
      },
      fail
    );
    loadBeneficiaryBanks(
      nextCountry,
      banks => {
        setBankOptions(banks);
        banksDone = true;
        finish();
      },
      fail
    );
  };

  useEffect(() => {
    if (!isBankAccountMode(mode) || !contact || fields.length || loading) {
      return;
    }
    if (mode === 'EDIT_BANK_ACCOUNT' && !bankAccount) {
      return;
    }
    loadForm(bankAccount?.country || contact.country, contact.flow_type);
  }, [bankAccount?.id, contact?.id, mode]);

  return {
    step,
    country,
    flowType,
    fields,
    bankOptions,
    fieldValues,
    loading,
    loadError,
    defaultCurrency,
    currencyOptions,
    enabledCountryConfigurations: createPayout.enabledCountryConfigurations,
    initialPayoutData,
    setCountry,
    setFlowType,
    setFieldValues,
    openBankData: () => loadForm(),
    backToCountry: () => setStep('country'),
  };
};
